// Owner Dashboard PDF export — client-side via html2canvas + jsPDF.
// Captures the current DOM (with all dynamic colours) into a multi-page PDF.

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";

const A4 = { w: 595.28, h: 841.89 }; // pt — jsPDF default
const MARGIN = 24;
const HEADER_H = 56;
const FOOTER_H = 22;

function formatDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function drawHeader(pdf, meta, pageNum, pageCount) {
  pdf.setFillColor(248, 250, 252);
  pdf.rect(0, 0, A4.w, HEADER_H, "F");
  pdf.setFontSize(13);
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.text(meta.title || "CertOpz · Owner Dashboard", MARGIN, 24);
  pdf.setFontSize(8.5);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(100, 116, 139);
  const sub = [
    meta.tenant && `Tenant: ${meta.tenant}`,
    `Period: ${meta.period}`,
    meta.financialYearId ? `FY: ${meta.financialYearId.slice(0, 8)}` : null,
    `BU: ${meta.businessUnitLabel || "All"}`,
    `Generated: ${meta.generatedAt.toLocaleString()}`,
  ].filter(Boolean).join("   ·   ");
  pdf.text(sub, MARGIN, 40);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Page ${pageNum} / ${pageCount}`, A4.w - MARGIN, 40, { align: "right" });
}

function drawFooter(pdf) {
  pdf.setFontSize(7.5);
  pdf.setTextColor(148, 163, 184);
  pdf.text("CertOpz ERP · Confidential", MARGIN, A4.h - 12);
  pdf.text(`Exported ${new Date().toLocaleString()}`, A4.w - MARGIN, A4.h - 12, { align: "right" });
}

async function logAudit(meta) {
  // Best-effort — never blocks the download
  try {
    await api.post("/admin-settings/audit/dashboard-export", {
      period: meta.period,
      financialYearId: meta.financialYearId || null,
      businessUnitId: meta.businessUnitId || null,
    });
  } catch {
    // Endpoint may not exist; that's OK
  }
}

/**
 * Render the given DOM node to a multi-page PDF.
 * @param {HTMLElement} node — element ref/id="owner-dashboard-export-root"
 * @param {object} meta — { title, tenant, period, financialYearId, businessUnitId, businessUnitLabel, generatedAt }
 */
export async function exportDashboardToPdf(node, meta) {
  if (!node) throw new Error("Export root not found.");

  // Snapshot with full computed colours. Background matches the
  // dashboard canvas token (`--dashboard-bg`, #F8FAFC) so the
  // exported PDF visually mirrors the on-screen surface.
  const canvas = await html2canvas(node, {
    backgroundColor: "#F8FAFC",
    scale: 2,
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight,
  });

  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });

  // Content area
  const contentW = A4.w - MARGIN * 2;
  const contentH = A4.h - HEADER_H - FOOTER_H - MARGIN;
  const imgWPx = canvas.width;
  const imgHPx = canvas.height;
  const scale = contentW / imgWPx;
  const scaledFullH = imgHPx * scale;

  const pageImgH = contentH / scale;     // src pixels per page
  const totalPages = Math.max(1, Math.ceil(imgHPx / pageImgH));

  for (let p = 0; p < totalPages; p++) {
    if (p > 0) pdf.addPage();
    drawHeader(pdf, meta, p + 1, totalPages);

    // Slice src canvas onto a temp canvas, then add as image
    const srcY = p * pageImgH;
    const sliceH = Math.min(pageImgH, imgHPx - srcY);
    const tmp = document.createElement("canvas");
    tmp.width = imgWPx;
    tmp.height = sliceH;
    const ctx = tmp.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, imgWPx, sliceH);
    ctx.drawImage(canvas, 0, srcY, imgWPx, sliceH, 0, 0, imgWPx, sliceH);
    const dataUrl = tmp.toDataURL("image/png", 0.92);

    const drawH = sliceH * scale;
    pdf.addImage(dataUrl, "PNG", MARGIN, HEADER_H + 8, contentW, drawH, undefined, "FAST");
    drawFooter(pdf);
  }

  // Suppress next-line warning about unused variable on some builds
  // eslint-disable-next-line no-unused-vars
  const _unused = scaledFullH;

  const fname = `CertOpz_Owner_Dashboard_${meta.period}_${formatDate(meta.generatedAt)}.pdf`;
  pdf.save(fname);

  // Best-effort audit log (non-blocking)
  logAudit(meta);
  return fname;
}
